import { isMobile } from '../utils/index.ts';

interface JoystickData {
  x: number;
  y: number;
}
interface Joystick {
  container: HTMLElement;
  handle: HTMLElement;
  centerX: number;
  centerY: number;
  maxDistance: number;
  isVisible: boolean;
  isActive: boolean;
  defaultLeft: number;
  defaultBottom: number;
}

interface TouchAreas {
  left: HTMLElement;
  right: HTMLElement;
  top: HTMLElement;
}

export class MobileController {
  private container: HTMLElement;
  private joystick: Joystick;
  private runButton: HTMLElement;
  private touchAreas: TouchAreas;
  private isRunning: boolean;
  private joystickData: JoystickData;
  private isMobile: boolean;
  private isPortrait: boolean;
  private handleOrientationChange?: () => void;

  constructor(container: HTMLElement) {
    this.container = container.parentNode as unknown as HTMLElement;
    this.joystick = null as unknown as Joystick;
    this.runButton = null as unknown as HTMLElement;
    this.touchAreas = null as unknown as TouchAreas;
    this.isRunning = true; // 默认为跑步状态
    this.joystickData = { x: 0, y: 0 };
    this.isMobile = isMobile();
    this.isPortrait = window.innerHeight > window.innerWidth; // 检测是否为竖屏

    if (this.isMobile) {
      this.createMobileControls();
      this.bindOrientationChange();
    }
  }

  createMobileControls(): void {
    this.createJoystick();
    this.createTouchAreas();
    // this.createRunButton();
  }

  createTouchAreas(): void {
    // 创建左侧摇杆区域
    const leftArea = document.createElement('div');
    leftArea.setAttribute('id', 'leftArea');

    // 创建右侧摄像头控制区域
    const rightArea = document.createElement('div');

    // 创建上半屏摄像头控制区域（仅竖屏时使用）
    const topArea = document.createElement('div');

    this.container.appendChild(leftArea);
    this.container.appendChild(rightArea);
    this.container.appendChild(topArea);

    this.touchAreas = {
      left: leftArea,
      right: rightArea,
      top: topArea,
    };

    this.updateTouchAreasLayout();
    this.addTouchAreaEvents();
  }
  /**
   * 根据屏幕方向更新触摸区域布局
   */
  updateTouchAreasLayout(): void {
    if (this.isPortrait) {
      // 竖屏模式：左侧40%区域分为上下两部分
      // 左下区域：摇杆控制（左侧40%的下半屏）
      this.touchAreas.left.style.cssText = `
        position: fixed;
        top: 50%;
        left: 10px;
        width: 40%;
        height: 50vh;
        z-index: 999;
        touch-action: none;
        pointer-events: auto;
      `;

      // 右侧区域：摄像头控制（右侧60%的全屏）
      this.touchAreas.right.style.cssText = `
        position: fixed;
        top: 0;
        right: 0;
        width: 60%;
        height: 100vh;
        z-index: 999;
        touch-action: none;
        pointer-events: none;
      `;

      // 上半屏左侧区域：摄像头控制（左侧40%的上半屏）
      this.touchAreas.top.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 40%;
        height: 50vh;
        z-index: 999;
        touch-action: none;
        pointer-events: none;
      `;
    } else {
      // 横屏模式：保持原有布局
      // 左侧区域：摇杆控制（左侧40%的全屏）
      this.touchAreas.left.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 40%;
        height: 100vh;
        z-index: 999;
        touch-action: none;
        pointer-events: auto;
      `;

      // 右侧区域：摄像头控制（右侧60%的全屏）
      this.touchAreas.right.style.cssText = `
        position: fixed;
        top: 0;
        right: 0;
        width: 60%;
        height: 100vh;
        z-index: 999;
        touch-action: none;
        pointer-events: none;
      `;

      // 上半屏区域在横屏时隐藏
      this.touchAreas.top.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        z-index: 999;
        touch-action: none;
        pointer-events: none;
        display: none;
      `;
    }
  }

  /**
   * 绑定屏幕方向变化事件
   */
  bindOrientationChange(): void {
    const handleOrientationChange = () => {
      const wasPortrait = this.isPortrait;
      this.isPortrait = window.innerHeight > window.innerWidth;

      // 只有当方向真正改变时才更新布局
      if (wasPortrait !== this.isPortrait) {
        this.updateTouchAreasLayout();
      }
    };
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
  }

  createJoystick(): void {
    // 创建摇杆容器（默认显示在左下角，半透明且不参与触控）
    const joystickContainer = document.createElement('div');
    joystickContainer.style.cssText = `
      position: fixed;
      bottom: 50px;
      left: 50px;
      width: 120px;
      height: 120px;
      background: rgba(255, 255, 255, 0.15);
      border: 2px solid rgba(255, 255, 255, 0.25);
      border-radius: 50%;
      z-index: 1001;
      touch-action: none;
      display: block;
      pointer-events: none;
      opacity: 0.4;
    `;

    // 创建摇杆手柄
    const joystickHandle = document.createElement('div');
    joystickHandle.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.4);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: none;
    `;

    joystickContainer.appendChild(joystickHandle);
    this.container.appendChild(joystickContainer);

    this.joystick = {
      container: joystickContainer,
      handle: joystickHandle,
      centerX: 60,
      centerY: 60,
      maxDistance: 40,
      isVisible: false, // 初始状态为不可见（不参与交互）
      isActive: false, // 新增：标记摇杆是否激活
      defaultLeft: 50,
      defaultBottom: 50,
    };
  }

  createRunButton(): void {
    const runButton = document.createElement('div');
    runButton.innerHTML = '走'; // 默认显示"走"（因为默认状态是跑步）
    runButton.style.cssText = `
      position: fixed;
      bottom: 50px;
      right: 50px;
      width: 80px;
      height: 80px;
      background: rgba(255, 255, 255, 0.3);
      border: 2px solid rgba(255, 255, 255, 0.5);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: bold;
      color: white;
      z-index: 1000;
      touch-action: none;
      user-select: none;
    `;

    this.container.appendChild(runButton);
    this.runButton = runButton;

    this.addRunButtonEvents();
  }

  addTouchAreaEvents(): void {
    let isDragging = false;
    let currentTouchId: number | null = null;

    const handleTouchStart = (e: TouchEvent): void => {
      // 不阻止默认行为，让右侧摄像头控制正常工作
      // 遍历所有触摸点，找到在左侧区域的触摸
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];

        // 检查触摸点是否在左侧区域内
        const leftAreaRect = this.touchAreas.left.getBoundingClientRect();
        if (
          touch.clientX >= leftAreaRect.left &&
          touch.clientX <= leftAreaRect.right &&
          touch.clientY >= leftAreaRect.top &&
          touch.clientY <= leftAreaRect.bottom
        ) {
          // 如果已经有摇杆在拖拽，忽略新的触摸
          if (isDragging && currentTouchId !== null) {
            continue;
          }

          e.preventDefault(); // 只对左侧触摸阻止默认行为
          currentTouchId = touch.identifier;

          // 激活摇杆并移动到触摸位置
          this.activateJoystickAt(touch.clientX, touch.clientY);
          isDragging = true;
          break; // 找到一个有效触摸就退出循环
        }
      }
    };

    const handleTouchMove = (e: TouchEvent): void => {
      if (!isDragging || !this.joystick.isActive || currentTouchId === null) return;

      // 找到对应的触摸点
      let touch: Touch | null = null;
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === currentTouchId) {
          touch = e.touches[i];
          break;
        }
      }
      if (!touch) return;

      // 只对摇杆相关的触摸阻止默认行为
      e.preventDefault();
      this.updateJoystickPosition(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = (e: TouchEvent): void => {
      if (!isDragging || currentTouchId === null) return;

      // 检查是否是当前的触摸点结束
      let touchEnded = true;
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === currentTouchId) {
          touchEnded = false;
          break;
        }
      }

      if (touchEnded) {
        e.preventDefault();
        this.deactivateJoystick();
        isDragging = false;
        currentTouchId = null;
      }
    };

    // 在整个文档上监听所有触摸事件，但只处理左侧区域的触摸
    document.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
  }

  activateJoystickAt(x: number, y: number): void {
    // 激活摇杆并移动到触摸位置
    this.joystick.isActive = true;
    this.joystick.isVisible = true;

    // 更新摇杆样式为激活状态
    this.joystick.container.style.background = 'rgba(255, 255, 255, 0.3)';
    this.joystick.container.style.border = '2px solid rgba(255, 255, 255, 0.5)';
    this.joystick.container.style.opacity = '1';
    this.joystick.container.style.pointerEvents = 'auto';
    this.joystick.handle.style.background = 'rgba(255, 255, 255, 0.8)';

    // 移动摇杆到触摸位置
    this.joystick.container.style.left = `${x}px`;
    this.joystick.container.style.top = `${y}px`;
    this.joystick.container.style.bottom = 'auto';
    this.joystick.container.style.transform = 'translate(-50%, -50%)';

    // 重置手柄到中心
    this.joystick.handle.style.transform = 'translate(-50%, -50%)';
    this.joystickData.x = 0;
    this.joystickData.y = 0;
  }

  updateJoystickPosition(touchX: number, touchY: number): void {
    if (!this.joystick.isVisible || !this.joystick.isActive) return;

    const rect = this.joystick.container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const x = touchX - centerX;
    const y = touchY - centerY;

    const distance = Math.sqrt(x * x + y * y);
    const { maxDistance } = this.joystick;

    let finalX = x;
    let finalY = y;

    if (distance > maxDistance) {
      finalX = (x / distance) * maxDistance;
      finalY = (y / distance) * maxDistance;
    }

    // 更新手柄位置
    this.joystick.handle.style.transform = `translate(calc(-50% + ${finalX}px), calc(-50% + ${finalY}px))`;

    // 计算归一化的摇杆数据
    this.joystickData.x = finalX / maxDistance;
    this.joystickData.y = -finalY / maxDistance; // 反转Y轴
  }

  deactivateJoystick(): void {
    // 停用摇杆
    this.joystick.isActive = false;
    this.joystick.isVisible = false;

    // 恢复摇杆为半透明状态
    this.joystick.container.style.background = 'rgba(255, 255, 255, 0.15)';
    this.joystick.container.style.border = '2px solid rgba(255, 255, 255, 0.25)';
    this.joystick.container.style.opacity = '0.4';
    this.joystick.container.style.pointerEvents = 'none';
    this.joystick.handle.style.background = 'rgba(255, 255, 255, 0.4)';

    // 摇杆回到默认位置
    this.joystick.container.style.left = `${this.joystick.defaultLeft}px`;
    this.joystick.container.style.top = 'auto';
    this.joystick.container.style.bottom = `${this.joystick.defaultBottom}px`;
    this.joystick.container.style.transform = 'none';

    // 重置手柄到中心
    this.joystick.handle.style.transform = 'translate(-50%, -50%)';

    // 重置摇杆数据
    this.joystickData.x = 0;
    this.joystickData.y = 0;
  }
  addRunButtonEvents(): void {
    const handlePress = (e: Event): void => {
      e.preventDefault();
      this.isRunning = !this.isRunning;

      // 更新按钮显示和样式
      if (!this.isRunning) {
        // 当前状态为跑步，按钮显示"跑"，背景变红
        this.runButton.innerHTML = '跑';
        this.runButton.style.background = 'rgba(255, 100, 100, 0.6)';
      } else {
        // 当前状态为走路，按钮显示"走"，背景为默认
        this.runButton.innerHTML = '走';
        this.runButton.style.background = 'rgba(255, 255, 255, 0.3)';
      }
    };

    this.runButton.addEventListener('touchstart', handlePress, {
      passive: false,
    });
    this.runButton.addEventListener('click', handlePress);
  }
  getMovementInput(): { x: number; y: number } {
    if (!this.isMobile) return { x: 0, y: 0 };
    return { ...this.joystickData };
  }

  getRunState(): boolean {
    if (!this.isMobile) return false;
    return this.isRunning;
  }

  isMobileDevice(): boolean {
    return this.isMobile;
  }

  destroy(): void {
    if (this.touchAreas) {
      this.touchAreas.left.remove();
      this.touchAreas.right.remove();
      if (this.touchAreas.top) {
        this.touchAreas.top.remove();
      }
    }
    if (this.joystick) {
      this.joystick.container.remove();
    }
    if (this.runButton) {
      this.runButton.remove();
    }

    // 移除事件监听器
    window.removeEventListener('resize', this.handleOrientationChange!);
    window.removeEventListener('orientationchange', this.handleOrientationChange!);
  }
}
